import Logger from '../../utils/logger';
import EventEmitter from '../../utils/event-emitter';

/**
 * Maintains a timeline of items on the current playback source
 */
export default class Timeline extends EventEmitter {
  static get defaultOptions () {
    return {
      logger: Logger
    };
  }

  constructor (options) {
    super();

    options = Object.assign({}, Timeline.defaultOptions, options);

    this._logger = options.logger;
    this._index = null;
    this._entries = [];
    this._position = 0;
    this._duration = Infinity;
    this._threshold = 1.5;
  }

  /**
   * Retrieve a list of all entries on the timeline
   * @return {object[[]}
   */
  all () {
    return this._entries.slice(0);
  }

  /**
   * Retrieve the current entry on the timeline based on player position
   * @return {object}
   */
  current () {
    return this._entries[this._index];
  }

  /**
   * Insert an entry on to the timeline
   * @param {object} entry
   */
  insert (entry) {
    for (let i = 0, len = this._entries.length; i < len; i++) {
      const next = this._entries[i];
      const startTime = entry.startTime === -1 ? this._duration : entry.startTime;
      const nextStartTime = next.startTime === -1 ? this._duration : next.startTime;

      // insert the entry inbetween existing items when the entry start time
      // is less than the current entry in the lopp
      if (startTime < nextStartTime) {
        this._entries.splice(i, 0, entry);
        return;
      }
    }

    // when no entries can be found after the new one then it is assumed the new one
    // is the new last item in the list and is appended
    this._entries.push(entry);
  }

  /**
   * Remove an entry from the timeline
   * @param  {object} entry The entry to be removed
   */
  remove (entry) {
    const index = this._entries.indexOf(entry);
    if (index !== -1) {
      this._entries.splice(index, 1);
    }
  }

  /**
   * Clear the current timeline
   */
  clear () {
    // this._index = null;
    // this._position = 0;
    // this._duration = Infinity;
    this.reset();
    this._entries = [];
  }

  /**
   * Reset the timeline
   * Used when live sources are stopped
   */
  reset () {
    this._index = null;
    this._position = 0;
    this._duration = Infinity;
  }

  /**
   * Update the current timeline duration
   *
   * If the duration given is less than or equal to zero, it will instead update the timeline to an Infinity duration. This is
   * important, as not doing so will make the timeline unable to properly determine the current entry on live sources.
   *
   * @param {integer} ms New duration in seconds
   */
  setDuration (duration) {
    this._duration = duration <= 0 ? Infinity : duration;
  }

  /**
   * Update the current timeline position
   *
   * If the given position exceeds the timeline duration, it will instead use the timeline duration.
   *
   * @param {integer} ms New position in seconds
   */
  setPosition (position) {
    this._position = Math.min(position, this._duration);
    this._update();
  }

  /**
   * Compare two entries to check if they match
   * @param  {object}  entry1
   * @param  {object}  entry2
   * @return {boolean}
   */
  _compare (entry1, entry2) {
    if (!entry2) return false;

    if (entry1.metadata && entry2.metadata) {
      if (entry1.metadata.title && entry1.metadata.title === entry2.metadata.title) {
        return true;
      }

      if (entry1.metadata.adId && entry1.metadata.adId === entry2.metadata.adId) {
        return true;
      }
    }

    if (entry1.action && entry2.action) {
      return entry1.action === entry2.action && entry1.startTime === entry2.startTime;
    }

    return false;
  }

  /**
   * Checks if an entry should be the active one
   * @param {object} entry
   */
  _isActiveEntry (entry) {
    let startTime = entry.startTime;
    let endTime = entry.metadata && entry.metadata.duration;

    if (startTime === -1) {
      startTime = this._duration;
    }

    // actions are only triggered once the client reaches within a threshold of the target position
    if (entry.action) {
      const diff = Math.abs(startTime - this._position);
      if (diff <= this._threshold) {
        return true;
      }
      return false;
    }

    if (!endTime) {
      endTime = this._duration || Infinity;
    }

    // return true when the current position lies somewhere inside the entry region
    return (startTime <= this._position && (endTime > this._position || endTime >= this._duration));
  }

  /**
   * Update the current timeline entry based on the current position and duration
   */
  _update () {
    let foundIndex = null;
    const foundActions = [];

    for (let i = 0, len = this._entries.length; i < len; i++) {
      const entry = this._entries[i];

      // check if the entry at the current index should be active
      if (this._isActiveEntry(entry)) {
        if (entry.action) {
          // push actions onto the found actions list
          foundActions.push(entry);
          // remove actions from the timeline once they've been executed
          this._entries.splice(i--, 1);
          // update iteration counters to account for the entry list size reduction
          len--;
        } else {
          foundIndex = i;
        }
        // check the next entry
        continue;
      }

      // this entry appears later in the timeline than the current position
      // so break the loop here to save perf. entries should be sorted by start time
      // so breaking here should be fine
      if (entry.startTime > this._position + this._threshold) {
        break;
      }
    }

    // handle any actions that should be triggered by this update
    for (let i = 0, len = foundActions.length; i < len; i++) {
      const entry = foundActions[i];
      switch (entry.action) {
        case Timeline.ACTION_AD_STOP:
          this.emit({
            type: Timeline.EVENT_ACTION,
            entry: entry
          });
          break;

        case Timeline.ACTION_AD_START:
        case Timeline.ACTION_CLEAR:
          this.emit({
            type: Timeline.EVENT_ACTION,
            entry: entry
          });
          break;

        case Timeline.ACTION_AD_REQUEST:
          this.emit({
            type: Timeline.EVENT_ACTION,
            entry: entry
          });
          break;
      }
    }

    // handle updating the current metadata index
    const foundEntry = this._entries[foundIndex];
    const currentEntry = this.current();
    if (foundIndex !== this._index) {
      this._index = foundIndex;
      if (foundEntry !== currentEntry) {
        this.emit({
          type: Timeline.EVENT_CHANGE,
          entry: foundIndex === null ? null : this._entries[foundIndex]
        });
      }
    }
  }
}

Timeline.EVENT_ACTION = 'action';
Timeline.EVENT_CHANGE = 'change';
Timeline.ACTION_AD_START = 'AD_START';
Timeline.ACTION_AD_STOP = 'AD_STOP';
Timeline.ACTION_AD_REQUEST = 'AD_REQUEST';
Timeline.ACTION_CLEAR = 'CLEAR';
